import React, { useState, useEffect, useRef } from 'react';
import cn from 'classnames';
import styles from './CurrencyDropdown.module.sass';
import Icon from '../../../components/Icon';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { coinIcons } from '../CurrencyHelper/currenciesIcons';
import { getFavorites } from '../CurrencyHelper/favourite';
import { navigation } from '../CurrencyHelper/navigation';
import CurrencyRow from '../CurrencyHelper/CurrencyRow';


const CurrencyDropdown = ({ onMarketSelect, selectedMarket }) => {
  const { t } = useTranslation();
  const [activeIndex, setActiveIndex] = useState(0);
  const [search, setSearch] = useState('');
  const [favoritesData, setFavoritesData] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedCurrency, setSelectedCurrency] = useState(selectedMarket);
  const dropdownRef = useRef(null);

  const markets = useSelector((state) => state.crypto.markets.markets);

  // Add click outside handler
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

// Update selected currency when selectedMarket changes
  useEffect(() => {
    setSelectedCurrency(selectedMarket);
  }, [selectedMarket]);

// Update favorites based on local storage
  const updateFavorites = () => {
    const favorites = getFavorites();
    const favoriteMarkets = markets?.filter((market) => favorites.includes(market._id));
    setFavoritesData(favoriteMarkets);
  };

  // Update favorites every time the markets change or a favorite is toggled
  useEffect(() => {
    updateFavorites();
  }, [markets]);

  // Filter markets based on the active tab and search term
  const filteredMarkets = markets
    ?.filter((market) => {
      const matchesSearch = market?.pairName
        ?.toLowerCase()
        .includes(search.replace(/-/g, '/').toLowerCase());
      if (activeIndex === 0 || activeIndex === 2) {
        // All and Top: show all markets (filtered by search)
        return matchesSearch;
      }
      if (activeIndex === 1) {
        // Favorite: show only markets in favorites that match the search
        const isFav = favoritesData.some((fav) => fav?._id === market?._id);
        return isFav && matchesSearch;
      }
      return false;
    })
    .sort((a, b) => {
      if (activeIndex === 2) {
        // Top: sort by price descending
        const priceA = parseFloat(a.currentPrice?.$numberDecimal || a.currentPrice || 0);
        const priceB = parseFloat(b.currentPrice?.$numberDecimal || b.currentPrice || 0);
        return priceB - priceA;
      }
      return 0;
    });

  return (
    <div className={styles.dropdown} ref={dropdownRef}>
<button 
  className={styles.toggle}
  onClick={() => setIsOpen(!isOpen)}
>
  {selectedCurrency ? (
    <div className={styles.selectedCurrency}>
      <img
        src={coinIcons[selectedCurrency.split('-')[0]?.toLowerCase()]}
        alt={selectedCurrency}
        className={styles.selectedIcon}
      />
      <span>{selectedCurrency}</span>
    </div>
  ) : (
    t('Select Currency')
  )}
  <Icon name="arrow-down" size="32" />
</button>

      {
        <div className={cn(styles.content, { [styles.active]: isOpen })}>
          <div className={styles.nav}>
            {navigation?.map((item, index) => (
              <button
                className={cn(styles.link, { [styles.active]: index === activeIndex })}
                onClick={() => setActiveIndex(index)}
                key={index}
              >
                {t(item)}
              </button>
            ))}
          </div>

          <div className={styles.searchWrapper}>
            <input
              className={styles.input}
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search"
              autoFocus
            />
          </div>

          <div className={styles.list}>
            {filteredMarkets?.map((x, index) => (
              <CurrencyRow
                key={index}
                data={x}
                onMarketSelect={(id) => {
                  onMarketSelect(id);
                  setSelectedCurrency(x.instanceId);
                  setIsOpen(false);
                }}
                onFavoriteToggle={updateFavorites}
              />
            ))}
          </div>
        </div>
      }
    </div>
  );
};

export default CurrencyDropdown;